"use client";

import React, { useRef, useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
} from "@mui/material";

// Floating background animation
function FloatingPaths({ position }: { position: number }) {
  const paths = Array.from({ length: 36 }, (_, i) => ({
    id: i,
    d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${
      380 - i * 5 * position
    } -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${
      152 - i * 5 * position
    } ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${
      684 - i * 5 * position
    } ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
    width: 0.5 + i * 0.03,
  }));

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        marginTop: "-50%",
        backdropFilter: "blur(100px)",
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 696 316"
        fill="none"
        style={{ color: "#fad32e" }}
      >
        <title>Background Paths</title>
        {paths.map((path) => (
          <motion.path
            key={path.id}
            d={path.d}
            stroke="currentColor"
            strokeWidth={path.width}
            strokeOpacity={0.1 + path.id * 0.03}
            initial={{ pathLength: 0.3, opacity: 0.6 }}
            animate={{
              pathLength: 1,
              opacity: [0.3, 0.6, 0.3],
              pathOffset: [0, 1, 0],
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        ))}
      </svg>
    </Box>
  );
}

// Custom hook for scroll animations
function useScrollAnimation() {
  const [ref, setRef] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );
    
    if (ref) {
      observer.observe(ref);
    }
    
    return () => {
      if (ref) {
        observer.unobserve(ref);
      }
    };
  }, [ref]);
  
  return [setRef, isVisible];
}

const HashInfraLanding = ({pricingRef}) => {
  // Scroll animations for different sections
  const [heroTitleRef, heroTitleVisible] = useScrollAnimation();
  const [heroDescRef, heroDescVisible] = useScrollAnimation();
  const [heroButtonsRef, heroButtonsVisible] = useScrollAnimation();
  const [buildTitleRef, buildTitleVisible] = useScrollAnimation();
  const [buildDescRef, buildDescVisible] = useScrollAnimation();
  
  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "#000000",
        color: "#ffffff",
        minHeight: "100vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        py: 16,
        overflow: "hidden",
      }}
    >
      {/* Animated Background */}
      <Box sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
        <FloatingPaths position={1} />
        <FloatingPaths position={-1} />
      </Box>

      {/* Foreground Content */}
      <Container sx={{ position: "relative", zIndex: 10, mt: 5 }}>
        <Grid
          container
          direction="column"
          spacing={2}
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <Grid item xs={12}>
            <Box sx={{ textAlign: "left" }} ref={heroTitleRef}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ 
                  opacity: heroTitleVisible ? 1 : 0, 
                  y: heroTitleVisible ? 0 : 50 
                }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: "light",
                    fontSize: { xs: "2.5rem", md: "5rem" },
                    lineHeight: 1.2,
                    fontFamily: "PoppiboxShans, sans-serif",
                  }}
                >
                  Production-Ready
                  <br />
                  <Box component="span" sx={{ color: "#fad32e" }}>
                    Kubernetes
                  </Box>
                  <br />
                  in Minutes
                </Typography>
              </motion.div>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={8} alignItems="center">
              <Grid item>
                <Box sx={{ textAlign: "left" }} ref={heroDescRef}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ 
                      opacity: heroDescVisible ? 1 : 0, 
                      y: heroDescVisible ? 0 : 30 
                    }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        maxWidth: "650px",
                        fontWeight: "light",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: { xs: "0.9rem", md: "1.1rem" },
                      }}
                    >
                      HashInfra is a purpose-built infrastructure layer that helps
                      engineering teams automate their cloud-native deployments while
                      reducing overhead, improving security, and streamlining DevOps.
                    </Typography>
                  </motion.div>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              justifyContent="flex-start"
              alignItems="center"
              sx={{ textAlign: "center" }}
              ref={heroButtonsRef}
            >
              <Grid item>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: heroButtonsVisible ? 1 : 0, 
                    y: heroButtonsVisible ? 0 : 20 
                  }}
                  transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      bgcolor: "#fad32e",
                      color: "#000000",
                      px: 4,
                      fontFamily: "Poppins, sans-serif",
                      borderColor: "#fad32e",
                      fontWeight: "bold",
                      width: "200px",
                      height: "50px",
                      borderRadius: "14px",
                      "&:hover": {
                        bgcolor: "transparent",
                        color: "#ffffff",
                        borderColor: "#FFFFFF",
                      },
                    }}
                    onClick={() => {
                      window.open("https://calendly.com/clockhash/talk-to-us", "_blank");
                    }}
                  >
                    Request Demo
                  </Button>
                </motion.div>
              </Grid>
              <Grid item>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: heroButtonsVisible ? 1 : 0, 
                    y: heroButtonsVisible ? 0 : 20 
                  }}
                  transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      borderColor: "#ffffff",
                      color: "#000000",
                      px: 4,
                      bgcolor: "#FFFFFF",
                      width: "200px",
                      height: "50px",
                      borderRadius: "14px",
                      "&:hover": {
                        bgcolor: "transparent",
                        borderColor: "#ffffff",
                        color: "#ffffff",
                      },
                    }}
                    onClick={() => {
                      pricingRef.current.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    Subscribe
                  </Button>
                </motion.div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Container maxWidth="lg">
          <Grid container justifyContent="flex-end" mt={20}>
            <Grid item xs={12} md={8}>
              <Box sx={{ textAlign: 'right' }} ref={buildTitleRef}>
                <motion.div
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ 
                    opacity: buildTitleVisible ? 1 : 0, 
                    x: buildTitleVisible ? 0 : 100 
                  }}
                  transition={{ duration: 1, ease: "easeOut" }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: { xs: '3rem', md: '5.6rem' },
                      lineHeight: 1.1,
                      mb: 4,
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    <Box component="span" sx={{ color: '#fad32e', fontWeight: 200 }}>
                      BUILD
                    </Box>
                    <br />
                    WITH
                    <br />
                    HASHINFRA
                  </Typography>
                </motion.div>

                <Box sx={{ maxWidth: '650px', ml: 'auto' }} ref={buildDescRef}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ 
                      opacity: buildDescVisible ? 1 : 0, 
                      y: buildDescVisible ? 0 : 30 
                    }}
                    transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        textAlign: 'right',
                        fontWeight: 'light',
                        fontFamily: 'Poppins, sans-serif',
                        lineHeight: 1.5,
                      }}
                    >
                      We provide ready-to-use Kubernetes environments across AWS, GCP, and Azure — complete with built-in CI/CD
                      automation, security hardening, and reliable support. Whether you're a startup launching your first product or an enterprise scaling mission-critical services,
                      Hashinfra takes care of the DevOps complexity so your team can stay focused on shipping features.
                    </Typography>
                  </motion.div>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Container>
    </Box>
  );
};

export default HashInfraLanding;


// "use client";

// import React from "react";
// import { motion } from "framer-motion";
// import {
//   Box,
//   Typography,
//   Button,
//   Grid,
//   Container,
// } from "@mui/material";



// // Floating background animation
// function FloatingPaths({ position }: { position: number }) {
    
//   const paths = Array.from({ length: 36 }, (_, i) => ({
//     id: i,
//     d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${
//       380 - i * 5 * position
//     } -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${
//       152 - i * 5 * position
//     } ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${
//       684 - i * 5 * position
//     } ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
//     width: 0.5 + i * 0.03,
//   }));

//   return (
//     <Box
//       sx={{
//         position: "absolute",
//         inset: 0,
//         pointerEvents: "none",
//         marginTop: "-50%",
//         backdropFilter: "blur(100px)",
//        // marginRight: "10%",
//         //transform:'rotate(300deg)',
//       }}
//     >
//       <svg
//         width="100%"
//         height="100%"
//         viewBox="0 0 696 316"
//         fill="none"
//         style={{ color: "#fad32e" }}
//       >
//         <title>Background Paths</title>
//         {paths.map((path) => (
//           <motion.path
//             key={path.id}
//             d={path.d}
//             stroke="currentColor"
//             strokeWidth={path.width}
//             strokeOpacity={0.1 + path.id * 0.03}
//             initial={{ pathLength: 0.3, opacity: 0.6 }}
//             animate={{
//               pathLength: 1,
//               opacity: [0.3, 0.6, 0.3],
//               pathOffset: [0, 1, 0],
//             }}
//             transition={{
//               duration: 20 + Math.random() * 10,
//               repeat: Infinity,
//               ease: "linear",
//             }}
//           />
//         ))}
//       </svg>
//     </Box>
//   );
// }

// const HashInfraLanding = ({pricingRef}) => {
//   return (
//     <Box
//       sx={{
//         position: "relative",
//         bgcolor: "#000000",
//         color: "#ffffff",
//         minHeight: "100vh",
//         display: "flex",
//         alignItems: "flex-start",
//         justifyContent: "flex-start",
//         py: 16,
//         overflow: "hidden",
//       }}
//     >
//       {/* Animated Background */}
//       <Box sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
//         <FloatingPaths position={1} />
//         <FloatingPaths position={-1} />
//       </Box>

//       {/* Foreground Content */}
//       <Container sx={{ position: "relative", zIndex: 10,mt: 5 }}>
//         <Grid
//           container
//           direction="column"
//           spacing={2}
//           alignItems="flex-start"
//           justifyContent="flex-start"
//         >
//           <Grid item xs={12}>
//             <Box sx={{ textAlign: "left" }}>
//               <Typography
//                 variant="h2"
//                 component="h1"
//                 sx={{
//                   fontWeight: "light",
//                   fontSize: { xs: "2.5rem", md: "5rem" },
//                   lineHeight: 1.2,
//                   fontFamily: "PoppiboxShans, sans-serif",
//                 }}
//               >
//                 Production-Ready
//                 <br />
//                 <Box component="span" sx={{ color: "#fad32e",  }}>
//                   Kubernetes
//                 </Box>
//                 <br />
//                 in Minutes
//               </Typography>
//             </Box>
//           </Grid>

//           <Grid item xs={12}>
//             <Grid container spacing={8} alignItems="center">
              

//               <Grid item>
//                 <Box sx={{ textAlign: "left" }}>
//                   <Typography
//                     variant="body1"
//                     sx={{
//                       maxWidth: "650px",
//                       fontWeight: "light",
//                       fontFamily: "Poppins, sans-serif",
//                       fontSize: { xs: "0.9rem", md: "1.1rem" },
//                     }}
//                   >
//                     HashInfra is a purpose-built infrastructure layer that helps
//                     engineering teams automate their cloud-native deployments while
//                     reducing overhead, improving security, and streamlining DevOps.
//                   </Typography>
//                 </Box>
//               </Grid>
//             </Grid>
//           </Grid>

//           <Grid item xs={12}>
//             <Grid
//               container
//               spacing={2}
//               justifyContent="flex-start"
//               alignItems="center"
//               sx={{ textAlign: "center" }}
//             >
//               <Grid item>
//                 <Button
//                   variant="outlined"
//                   sx={{
//                     bgcolor: "#fad32e",
//                     color: "#000000",
//                     px: 4,
//                     fontFamily: "Poppins, sans-serif",
//                     borderColor: "#fad32e",
//                     fontWeight: "bold",
//                     width: "200px",
//                     height: "50px",
//                     borderRadius: "14px",
//                     "&:hover": {
//                       bgcolor: "transparent",
//                       color: "#ffffff",
//                       borderColor: "#FFFFFF",
//                     },
//                   }}
//                   onClick={() => {
//                     window.open("https://calendly.com/clockhash/talk-to-us", "_blank");
//                   }}
//                 >
//                   Request Demo
//                 </Button>
//               </Grid>
//               <Grid item>
//                 <Button
//                   variant="outlined"
//                   sx={{
//                     borderColor: "#ffffff",
//                     color: "#000000",
//                     px: 4,
//                     bgcolor: "#FFFFFF",
//                     width: "200px",
//                     height: "50px",
//                     borderRadius: "14px",
//                     "&:hover": {
//                       bgcolor: "transparent",
//                       borderColor: "#ffffff",
//                       color: "#ffffff",
//                     },
//                   }}
//                   onClick={() => {
//                     pricingRef.current.scrollIntoView({ behavior: "smooth" });
//                   }}
//                 >
//                   Subscribe
//                 </Button>
//               </Grid>
//             </Grid>
//           </Grid>
//         </Grid>
//         <Container maxWidth="lg">
//                 <Grid container justifyContent="flex-end" mt={20} >
//                   <Grid item xs={12} md={8}>
//                     <Box sx={{ textAlign: 'right' }}>
//                       <Typography
//                         variant="h1"
//                         sx={{
//                           fontWeight: 'bold',
//                           fontSize: { xs: '3rem', md: '5.6rem' },
//                           lineHeight: 1.1,
//                           mb: 4,
//                           fontFamily: 'Poppins, sans-serif',
//                         }}
//                       >
//                         <Box component="span" sx={{ color: '#fad32e', fontWeight: 200 }}>
//                           BUILD
//                         </Box>
//                         <br />
//                         WITH
//                         <br />
//                         HASHINFRA
//                       </Typography>
        
//                       <Box sx={{ maxWidth: '650px', ml: 'auto' }}>
//                         <Typography
//                           variant="body1"
//                           sx={{
//                             fontSize: { xs: '0.875rem', md: '1rem' },
//                            // mb: 2,
//                            textAlign: 'right',
//                             fontWeight: 'light',
//                             fontFamily: 'Poppins, sans-serif',
//                             lineHeight: 1.5,
//                           }}
//                         >
//                           We provide ready-to-use Kubernetes environments across AWS, GCP, and Azure — complete with built-in CI/CD
//                           automation, security hardening, and reliable support.Whether you're a startup launching your first product or an enterprise scaling mission-critical services,
//                           Hashinfra takes care of the DevOps complexity so your team can stay focused on shipping features.
//                         </Typography>
                        
                        
//                       </Box>
//                     </Box>
//                   </Grid>
//                 </Grid>
//               </Container>
//       </Container>
//     </Box>
//   );
// };

// export default HashInfraLanding;
